import { Link, useNavigate } from "react-router-dom";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "./../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Modifier } from "./initializeModifier.js";
import CategoryModal from "../../components/CustomComponents/modal/parentCategoryModel";
import ModifierSelectionModal from "../../components/CustomComponents/modal/modifierSelectionModal.jsx";
import { appSettings } from "../../app/appSettingsSlice";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function AddModifierGroup() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { settingsData, profileData } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [compCurrency, setCompCurrency] = useState("GBP");

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  

  const [name, setName] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [type, setType] = useState("List");
  const [canSkip, setCanSkip] = useState(true);
  const [enableQuantity, setEnableQuantity] = useState(true);
  const [minimumSelection, setMinimumSelection] = useState(0);
  const [maximumSelection, setMaximumSelection] = useState(0);
  const [freeSelection, setFreeSelection] = useState(0);
  const [extraSelectionCharges, setExtraSelectionCharges] = useState(0);
  const [settings, setSettings] = useState({ s_VAT: "", isTaxEnabled: false });
  const [modifiers, setModifiers] = useState([
    {
      ...Modifier,
      menuItem_SalePrice: "",
      menuItem_CostPrice: "",
      menuItem_DeliveryPrice: "",
      menuItem_TakeAwayPrice: "",
      menuItem_TaxPercentage: "",
      reportCat_ID: "", // Explicitly clear
      reportCat_Name: "", // Explicitly clear
      rootCat_ID: "", // Explicitly clear
      rootCat_Name: "", // Explicitly clear
    },
  ]);
  const [activeRow, setActiveRow] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [existingModifierIds, setExistingModifierIds] = useState(new Set());
  const [priceChangedModifiers, setPriceChangedModifiers] = useState(new Set());
  const [editingValues, setEditingValues] = useState({});
  const [categories, setCategories] = useState([]);
  const [modals, setModals] = useState({
    showCategoryModal: false,
    showBulkModifierModal: false,
  });
  const [addNewRows, setAddNewRows] = useState(new Set());
  const [errors, setErrors] = useState({ minimum: '', maximum: '', free: '' });

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const settingsRef = useRef({ s_VAT: "", isTaxEnabled: false });
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      name: name || "",
      subTitle: subTitle || "",
      type: type || "List",
      canSkip: Boolean(canSkip),
      enableQuantity: Boolean(enableQuantity),
      minimumSelection: Number(minimumSelection) || 0,
      maximumSelection: Number(maximumSelection) || 0,
      freeSelection: Number(freeSelection) || 0,
      extraSelectionCharges: Number(extraSelectionCharges) || 0,
      modifiers: (modifiers || []).map((m) => ({
        menuItem_Name: m?.menuItem_Name || "",
        menuItem_SalePrice: m?.menuItem_SalePrice ?? "",
        menuItem_CostPrice: m?.menuItem_CostPrice ?? "",
        menuItem_DeliveryPrice: m?.menuItem_DeliveryPrice ?? "",
        menuItem_TakeAwayPrice: m?.menuItem_TakeAwayPrice ?? "",
        menuItem_TaxPercentage: m?.menuItem_TaxPercentage ?? "",
        reportCat_ID: m?.reportCat_ID || m?.rootCat_ID || "",
        menuItem_ID: m?.menuItem_ID || 0,
        is_OutOfStock: Boolean(m?.is_OutOfStock),
        menuItem_IsDefaultModifier: Boolean(m?.menuItem_IsDefaultModifier),
      })),
    });
  }, [
    name,
    subTitle,
    type,
    canSkip,
    enableQuantity,
    minimumSelection,
    maximumSelection,
    freeSelection,
    extraSelectionCharges,
    modifiers,
  ]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const findCategoryById = useCallback((cats, categoryId) => {
    if (!categoryId || !Array.isArray(cats) || cats.length === 0) return null;
    const target = Number(categoryId);
    for (const c of cats) {
      const cId = c?.rootCat_ID ?? c?.rC_ID ?? c?.category_ID ?? c?.id;
      if (cId === target || String(cId) === String(categoryId)) return c;
      const children = c?.subCategoriesList;
      if (Array.isArray(children) && children.length > 0) {
        const found = findCategoryById(children, categoryId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const getCategoryNameById = useCallback(
    (categoryId) => {
      if (
        !categoryId ||
        !Array.isArray(categories) ||
        categories.length === 0
      ) {
        return "";
      }

      const found = findCategoryById(categories, categoryId);
      return found?.rootCat_Name || found?.rC_Name || "";
    },
    [categories, findCategoryById]
  );

  const safeParseFloat = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "")
      return defaultVal;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const safeParseInt = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "")
      return defaultVal;
    const parsed = parseInt(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  useEffect(() => {
    settingsRef.current = settings;

    if (settings.isTaxEnabled && settings.s_VAT) {
      setModifiers((prevModifiers) =>
        prevModifiers.map((modifier) => {
          const shouldUpdate =
            !modifier.menuItem_TaxPercentage ||
            modifier.menuItem_TaxPercentage === 0 ||
            modifier.menuItem_TaxPercentage === "0" ||
            modifier.menuItem_TaxPercentage === "";

          return {
            ...modifier,
            menuItem_TaxPercentage: shouldUpdate
              ? settings.s_VAT
              : modifier.menuItem_TaxPercentage,
            menuItem_SalePrice:
              modifier.menuItem_SalePrice === 0
                ? ""
                : modifier.menuItem_SalePrice,
            menuItem_CostPrice:
              modifier.menuItem_CostPrice === 0
                ? ""
                : modifier.menuItem_CostPrice,
            menuItem_DeliveryPrice:
              modifier.menuItem_DeliveryPrice === 0
                ? ""
                : modifier.menuItem_DeliveryPrice,
            menuItem_TakeAwayPrice:
              modifier.menuItem_TakeAwayPrice === 0
                ? ""
                : modifier.menuItem_TakeAwayPrice,
          };
        })
      );
    }
  }, [settings]);

  const fetchCompCurrency = useCallback(async () => {
    try {
      // Use Redux state instead of API call
      if (!profileData) {
        setCompCurrency("GBP");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, [profileData]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      // Use Redux state instead of API call
      if (!settingsData) {
        const defaultSettings = { s_VAT: 0, isTaxEnabled: false };
        settingsRef.current = defaultSettings;
        setSettings(defaultSettings);
        setLoading(false);
        return;
      }

      const newSettings = {
        s_VAT: settingsData.s_VAT || 0,
        isTaxEnabled: settingsData.isTaxEnabled ?? false,
      };

      settingsRef.current = newSettings;
      setSettings(newSettings);
    } catch (err) {
      showErrorToast("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [settingsData]);

  const fetchCategories = useCallback(async (options = {}) => {
    const showGlobalLoading = options.showGlobalLoading !== false;
    if (!CompID) {
      return;
    }
    try {
      if (showGlobalLoading) setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });

      const categoryData = response.data.data || response.data;
      if (Array.isArray(categoryData)) {
        setCategories(categoryData);
      } else {
        setCategories([]);
      }
    } catch (err) {
      setCategories([]);
      showErrorToast("Failed to fetch categories");
    } finally {
      if (showGlobalLoading) setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    const handler = setTimeout(async () => {
      await fetchCompCurrency();
      await fetchSettings();
      fetchCategories();
    }, 500);
    return () => clearTimeout(handler);
  }, [fetchCompCurrency, fetchSettings, fetchCategories]);

  const validModifiersCount = useMemo(() => {
    return modifiers.filter(
      (modifier) => modifier.menuItem_Name && modifier.menuItem_Name.trim().length > 0
    ).length;
  }, [modifiers]);

  useEffect(() => {
    const min = parseInt(minimumSelection) || 0;
    const max = parseInt(maximumSelection) || 0;
    const free = parseInt(freeSelection) || 0;
    const num = validModifiersCount;

    let newErrors = { minimum: '', maximum: '', free: '' };

    if (min > max) {
      newErrors.minimum = "Minimum selection cannot be greater than maximum selection";
    }

    let maxErr = '';
    if (max < min) {
      maxErr = "Maximum selection must be greater than or equal to minimum selection";
    }
    if (max > num) {
      const msg = `Maximum selection cannot exceed the number of modifiers (${num})`;
      maxErr = maxErr ? `${maxErr}. ${msg}` : msg;
    }
    newErrors.maximum = maxErr;

    if (free > max) {
      newErrors.free = "Free selection must be less than or equal to maximum selection";
    }

    setErrors(newErrors);
  }, [minimumSelection, maximumSelection, freeSelection, validModifiersCount]);

  const handleNumericChange = (value, setter) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue < 0) {
      setter("0");
    } else {
      setter(value);
    }
  };

  const isExistingModifier = useCallback(
    (modifier) => {
      const id = Number(modifier?.menuItem_ID);
      return id > 0 && existingModifierIds.has(id);
    },
    [existingModifierIds]
  );

  const createEmptyModifierRow = useCallback(() => {
    const currentSettings = settingsRef.current;
    const defaultTaxValue =
      currentSettings.isTaxEnabled && currentSettings.s_VAT
        ? currentSettings.s_VAT
        : "";

    return {
      ...Modifier,
      menuItem_SalePrice: "",
      menuItem_CostPrice: "",
      menuItem_DeliveryPrice: "",
      menuItem_TakeAwayPrice: "",
      menuItem_TaxPercentage: defaultTaxValue,
      reportCat_ID: "",
      reportCat_Name: "",
      rootCat_ID: "",
      rootCat_Name: "",
    };
  }, []);

  const mapSelectedModifierToRow = useCallback(
    (selectedModifier) => {
      const inferredCatId =
        selectedModifier.rootCat_ID ??
        selectedModifier.category_ID ??
        selectedModifier.catID ??
        "";
      let inferredCatName = "";
      if (inferredCatId && Array.isArray(categories) && categories.length > 0) {
        const found = categories.find(
          (c) =>
            c.rootCat_ID === inferredCatId ||
            c.rootCat_ID === Number(inferredCatId)
        );
        inferredCatName = found?.rootCat_Name || "";
      }

      return {
        ...Modifier,
        ...selectedModifier,
        reportCat_ID:
          inferredCatId ||
          selectedModifier.reportCat_ID ||
          selectedModifier.rootCat_ID ||
          "",
        reportCat_Name:
          inferredCatName ||
          selectedModifier.reportCat_Name ||
          selectedModifier.rootCat_Name ||
          selectedModifier.category_Name ||
          "",
        rootCat_ID:
          inferredCatId ||
          selectedModifier.reportCat_ID ||
          selectedModifier.rootCat_ID ||
          "",
        rootCat_Name:
          inferredCatName ||
          selectedModifier.reportCat_Name ||
          selectedModifier.rootCat_Name ||
          selectedModifier.category_Name ||
          "",
      };
    },
    [categories]
  );

  const addedModifierIds = useMemo(
    () =>
      modifiers
        .map((m) => Number(m.menuItem_ID))
        .filter((id) => id > 0),
    [modifiers]
  );

  const handleModifierChange = (index, field, value) => {
    const currentModifier = modifiers[index];
    const existingRow = isExistingModifier(currentModifier);
    const protectedFields = [
      "menuItem_Name",
      "menuItem_SalePrice",
      "menuItem_CostPrice",
      "menuItem_TaxPercentage",
      "menuItem_DeliveryPrice",
      "menuItem_TakeAwayPrice",
      "reportCat_ID",
      "reportCat_Name",
      "rootCat_ID",
      "rootCat_Name",
    ];
    if (existingRow && protectedFields.includes(field)) {
      return;
    }

    let newValue = value;

    // Prevent negative values for price fields
    if (
      [
        "menuItem_SalePrice",
        "menuItem_CostPrice",
        "menuItem_DeliveryPrice",
        "menuItem_TakeAwayPrice",
      ].includes(field)
    ) {
      const numValue = parseFloat(value);
      if (value !== "" && (isNaN(numValue) || numValue < 0)) {
        newValue = "0";
      }
    } else if (field === "menuItem_TaxPercentage") {
      const numValue = parseFloat(value);
      if (value !== "" && (isNaN(numValue) || numValue < 0 || numValue > 100)) {
        newValue = Math.max(0, Math.min(100, numValue)).toString();
      }
    }

    setModifiers((prevModifiers) => {
      const currentModifier = prevModifiers[index];

      if (
        (field === "menuItem_SalePrice" ||
          field === "menuItem_CostPrice" ||
          field === "menuItem_TaxPercentage" ||
          field === "menuItem_DeliveryPrice" ||
          field === "menuItem_TakeAwayPrice") &&
        isExistingModifier(currentModifier)
      ) {
        if (!newValue || newValue === "" || newValue === "0" || newValue === 0) {
          setPriceChangedModifiers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(currentModifier.menuItem_ID);
            return newSet;
          });
        } else if (currentModifier[field] !== newValue) {
          setPriceChangedModifiers(
            (prev) => new Set([...prev, currentModifier.menuItem_ID])
          );
        }
      }

      const updatedModifiers = prevModifiers.map((modifier, i) =>
        i === index ? { ...modifier, [field]: newValue } : modifier
      );

      const shouldAddNewRow =
        index === prevModifiers.length - 1 &&
        ((field === "menuItem_Name" && newValue?.trim()) ||
          (field === "menuItem_SalePrice" && newValue && parseFloat(newValue) > 0) ||
          (field === "menuItem_DeliveryPrice" &&
            newValue &&
            parseFloat(newValue) > 0) ||
          (field === "menuItem_TakeAwayPrice" &&
            newValue &&
            parseFloat(newValue) > 0) ||
          (field === "menuItem_TaxPercentage" &&
            newValue &&
            parseFloat(newValue) > 0) ||
          (field === "menuItem_CostPrice" &&
            newValue &&
            parseFloat(newValue) > 0) ||
          (field === "reportCat_ID" && newValue));

      if (shouldAddNewRow) {
        const currentSettings = settingsRef.current;
        const defaultTaxValue =
          currentSettings.isTaxEnabled && currentSettings.s_VAT
            ? currentSettings.s_VAT
            : "";

        const newModifier = {
          ...Modifier,
          menuItem_SalePrice: "",
          menuItem_CostPrice: "",
          menuItem_DeliveryPrice: "",
          menuItem_TakeAwayPrice: "",
          menuItem_TaxPercentage: defaultTaxValue,
          reportCat_ID: "", // Explicitly clear
          reportCat_Name: "", // Explicitly clear
          rootCat_ID: "", // Explicitly clear
          rootCat_Name: "", // Explicitly clear
        }

        return [...updatedModifiers, newModifier];
      }

      return updatedModifiers;
    });
  };

  const handleRemoveModifier = (index) => {
    if (modifiers.length === 1) {
      showErrorToast("At least one modifier is required");
      return;
    }

    const modifierToRemove = modifiers[index];

    if (modifierToRemove.menuItem_ID) {
      const removedId = Number(modifierToRemove.menuItem_ID);
      if (existingModifierIds.has(removedId)) {
        setExistingModifierIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(removedId);
          return newSet;
        });
      }

      if (priceChangedModifiers.has(modifierToRemove.menuItem_ID)) {
        setPriceChangedModifiers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(modifierToRemove.menuItem_ID);
          return newSet;
        });
      }
    }

    const updatedModifiers = modifiers.filter((_, i) => i !== index);
    setModifiers(updatedModifiers);
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    try {
      setLoading(true);

      if (!name.trim()) {
        showErrorToast("Modifier Group Name is required");
        setLoading(false);
        return false;
      }

      const hasErrors = Object.values(errors).some(e => e);
      if (hasErrors) {
        showErrorToast("Please correct the validation errors");
        setLoading(false);
        return false;
      }

      let updatedModifiers = [...modifiers];
      updatedModifiers = updatedModifiers.filter(
        (modifier) =>
          modifier.menuItem_Name && modifier.menuItem_Name.trim().length > 0
      );

      if (updatedModifiers.length === 0) {
        showErrorToast("At least one modifier with a name is required");
        setLoading(false);
        return false;
      }

      const missingCategoryModifiers = updatedModifiers.filter(
        (modifier) => !modifier.reportCat_ID && !modifier.rootCat_ID
      );
      if (missingCategoryModifiers.length > 0) {
        showErrorToast("All modifiers must have a reporting category selected");
        setLoading(false);
        return false;
      }

      const min = parseInt(minimumSelection) || 0;
      const max = parseInt(maximumSelection) || 0;
      const free = parseInt(freeSelection) || 0;
      const numModifiers = updatedModifiers.length;

      updatedModifiers = updatedModifiers.map((item) => {
        const sale = safeParseFloat(item.menuItem_SalePrice);
        const delivery =
          item.menuItem_DeliveryPrice === undefined ||
          item.menuItem_DeliveryPrice === null ||
          item.menuItem_DeliveryPrice === ""
            ? sale
            : safeParseFloat(item.menuItem_DeliveryPrice);
        const takeAway =
          item.menuItem_TakeAwayPrice === undefined ||
          item.menuItem_TakeAwayPrice === null ||
          item.menuItem_TakeAwayPrice === ""
            ? sale
            : safeParseFloat(item.menuItem_TakeAwayPrice);

        const reportCatId = item.reportCat_ID || item.rootCat_ID || "";
        const reportCatName = item.reportCat_Name || item.rootCat_Name || "";

        return {
          menuItem_ID: item.menuItem_ID || 0,
          menuItem_Name: item.menuItem_Name || "",
          allergens: item.allergens || "",
          menuItem_IsModifier: true,
          menuItem_Type: "Modifier",
          compID: CompID,
          itsMasterProductID: safeParseInt(item.itsMasterProductID),
          MenuItem_AETime: item.MenuItem_AETime || "",
          MenuItem_ASTime: item.MenuItem_ASTime || "",
          MenuItem_BarCode: item.MenuItem_BarCode || "",
          MenuItem_Detail: item.MenuItem_Detail || "",
          MenuItem_Image: item.MenuItem_Image || "",
          MenuItem_ImageAltText: item.MenuItem_ImageAltText || "",
          MenuItem_KitchenDisplayName: item.MenuItem_KitchenDisplayName || "",
          MenuItem_Permalink: item.MenuItem_Permalink || "",
          MenuItem_PreparationTime: item.MenuItem_PreparationTime || "",
          MenuItem_RootType: item.MenuItem_RootType || "",
          MenuItem_SEODescription: item.MenuItem_SEODescription || "",
          MenuItem_SEOTitle: item.MenuItem_SEOTitle || "",
          MenuItem_SKU: item.MenuItem_SKU || "",
          MenuItem_ServingSize: item.MenuItem_ServingSize || "",
          menuItem_DeliveryPrice: delivery,
          menuItem_TakeAwayPrice: takeAway,
          menuItem_SalePrice: sale,
          menuItem_CostPrice: safeParseFloat(item.menuItem_CostPrice),
          menuItem_TaxPercentage: safeParseFloat(item.menuItem_TaxPercentage),
          reportCat_ID: reportCatId,
          reportCat_Name: reportCatName,
          is_OutOfStock: Boolean(item.is_OutOfStock),
          menuItem_IsDefaultModifier: Boolean(item.menuItem_IsDefaultModifier),
        };
      });

      const modifierGroupPayload = {
        CompID,
        modifierGroupID: 0,
        name: name,
        subTitle: subTitle,
        type: type,
        canSkip: canSkip,
        enableQuantity: enableQuantity,
        minimumSelection: min,
        maximumSelection: max,
        freeSelection: free,
        extraSelectionCharges: safeParseFloat(extraSelectionCharges),
        detail: "",
        updateDatetime: new Date().toISOString(),
        Modifiers: updatedModifiers,
      };

      const response = await axiosInstance.post(
        APIs.POST_ADD_MODIFIER_GROUP,
        modifierGroupPayload
      );

      if (response.data.status) {
        showSuccessToast("Modifier Group added successfully");
        initialFormSnapshotRef.current = getFormSnapshot();
        const newId =
          response.data?.data?.modifierGroupID ||
          response.data?.data?.ModifierGroupID ||
          response.data?.data?.mGID ||
          response.data?.data;
        if (exitAfterSave) {
          navigate("/modifiers/manageModifierGroups");
        } else if (newId && !Number.isNaN(Number(newId)) && Number(newId) > 0) {
          navigate(`/modifiers/updateModifierGroup/${newId}`);
        } else {
          navigate("/modifiers/manageModifierGroups");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to add Modifier Group");
        return false;
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
          "An error occurred while adding the Modifier Group"
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  const handleMultiModifierSelect = useCallback(
    (selectedModifiers) => {
      if (!Array.isArray(selectedModifiers) || selectedModifiers.length === 0) {
        return;
      }

      const newModifierIds = [];

      setModifiers((prevModifiers) => {
        const idsInTable = new Set(
          prevModifiers
            .map((m) => Number(m.menuItem_ID))
            .filter((id) => id > 0)
        );

        let base = [...prevModifiers];
        const lastRow = base[base.length - 1];
        const lastRowIsEmpty =
          !lastRow?.menuItem_Name?.trim() && !Number(lastRow?.menuItem_ID);
        if (lastRowIsEmpty && base.length > 0) {
          base = base.slice(0, -1);
        }

        const rowsToAdd = selectedModifiers
          .filter(
            (m) =>
              m?.menuItem_ID && !idsInTable.has(Number(m.menuItem_ID))
          )
          .map((m) => {
            newModifierIds.push(m.menuItem_ID);
            return mapSelectedModifierToRow(m);
          });

        return [...base, ...rowsToAdd, createEmptyModifierRow()];
      });

      if (newModifierIds.length > 0) {
        setExistingModifierIds(
          (prev) =>
            new Set([
              ...prev,
              ...newModifierIds.map((id) => Number(id)),
            ])
        );
        showSuccessToast(
          `${newModifierIds.length} existing modifier(s) added`
        );
      }
    },
    [createEmptyModifierRow, mapSelectedModifierToRow]
  );

  const handleNewModifierNameChange = useCallback(
    (index, value) => {
      if (isExistingModifier(modifiers[index])) {
        return;
      }
      setAddNewRows((prev) => new Set([...prev, index]));
      handleModifierChange(index, "menuItem_Name", value);
    },
    [modifiers, isExistingModifier]
  );

  useEffect(() => {
    if (
      !categories ||
      categories.length === 0 ||
      !modifiers ||
      modifiers.length === 0
    )
      return;
    let changed = false;
    const next = modifiers.map((m) => {
      if (
        m &&
        m.reportCat_ID &&
        !m.reportCat_Name &&
        m.menuItem_Name // Only update if modifier has a name
      ) {
        const found = findCategoryById(categories, m.reportCat_ID);
        if (found?.rootCat_Name || found?.rC_Name) {
          changed = true;
          return {
            ...m,
            reportCat_Name: found?.rootCat_Name || found?.rC_Name || "",
            rootCat_Name: found?.rootCat_Name || found?.rC_Name || "",
          };
        }
      }
      return m;
    });
    if (changed) setModifiers(next);
  }, [categories, modifiers, findCategoryById]);

  const hasPriceChanged = (modifier) => {
    return (
      !!modifier.menuItem_ID && priceChangedModifiers.has(modifier.menuItem_ID)
    );
  };

  const formatNumericValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    const numValue = Number(value);
    if (numValue === 0 || isNaN(numValue)) {
      return "";
    }

    return value.toString();
  };

  const NumericInput = ({
    value,
    onCommit,
    fieldKey,
    placeholder,
    readOnly,
    step = "any",
    hideZero = true,
    ...props
  }) => {
    let computed = value;
    if (hideZero) {
      if (
        computed === null ||
        computed === undefined ||
        computed === "" ||
        computed === 0 ||
        computed === "0" ||
        computed === "0.00"
      ) {
        computed = "";
      }
    }

    const displayValue = Object.prototype.hasOwnProperty.call(
      editingValues,
      fieldKey
    )
      ? editingValues[fieldKey]
      : computed;

    return (
      <input
        type="number"
        inputMode="decimal"
        step={step}
        min={0}
        value={displayValue}
        onChange={(e) => {
          const v = e.target.value;
          if (/^\d*(?:\.?\d*)?$/.test(v)) {
            setEditingValues((prev) => ({ ...prev, [fieldKey]: v }));
          }
        }}
        onKeyDown={(e) => {
          const allowed = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
          ];
          if (allowed.includes(e.key)) return;
          if (e.key === ".") {
            if (String(e.currentTarget.value).includes(".")) {
              e.preventDefault();
            }
            return;
          }
          if (e.key === "-") {
            e.preventDefault();
            return;
          }
          if (!/\d/.test(e.key)) {
            e.preventDefault();
          }
        }}
        onFocus={() => {
          setEditingValues((prev) => ({
            ...prev,
            [fieldKey]: displayValue ?? "",
          }));
        }}
        onBlur={(e) => {
          const finalVal = e.currentTarget.value;
          setEditingValues((prev) => {
            const { [fieldKey]: _omit, ...rest } = prev;
            return rest;
          });
          onCommit && onCommit(finalVal);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        className="form-control"
        style={{
          borderRadius: "10px",
          border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
          background: isDarkMode ? "#2d3436" : "#F9F9F9",
          color: isDarkMode ? "#ffffff" : undefined,
        }}
        {...props}
      />
    );
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = isDarkMode ? "#343a40" : "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const newModifiers = [...modifiers];
    const draggedModifier = newModifiers[draggedIndex];
    newModifiers.splice(draggedIndex, 1);
    newModifiers.splice(overIndex, 0, draggedModifier);

    setModifiers(newModifiers);

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const isSubmitDisabled = !name.trim() || !type;

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/modifiers/manageModifierGroups">
                Manage Modifier Groups
              </Link>
            </li>
            <li className="breadcrumb-item active">Add Modifier Group</li>
          </ol>
          <h1 className="page-header mb-0">Add Modifier Group</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/modifiers/manageModifierGroups")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
          disabled={isSubmitDisabled}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Modifier Group Details</PanelHeader>
          <PanelBody>
            <div
              className="p-4 rounded-3 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                border: isDarkMode ? "1px solid #404040" : undefined,
                color: isDarkMode ? "#ffffff" : undefined,
              }}
            >
              <div className="row mb-4">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Name"}
                      placeHolder={"Enter Modifier Group Name"}
                      type={"text"}
                      value={name}
                      onchange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Subtitle"}
                      placeHolder={"Enter Subtitle"}
                      type={"text"}
                      value={subTitle}
                      onchange={(e) => setSubTitle(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Minimum Selection"}
                      placeHolder={"Enter Minimum Selection"}
                      type={"number"}
                      min="0"
                      value={minimumSelection}
                      onchange={(e) => handleNumericChange(e.target.value, setMinimumSelection)}
                    />
                    {errors.minimum && <small className="text-danger d-block">{errors.minimum}</small>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Maximum Selection"}
                      placeHolder={"Enter Maximum Selection"}
                      type={"number"}
                      min="0"
                      value={maximumSelection}
                      onchange={(e) => handleNumericChange(e.target.value, setMaximumSelection)}
                    />
                    {errors.maximum && <small className="text-danger d-block">{errors.maximum}</small>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label={"Free Selection"}
                      placeHolder={"Enter Free Selection Count"}
                      type={"number"}
                      min="0"
                      value={freeSelection}
                      onchange={(e) => handleNumericChange(e.target.value, setFreeSelection)}
                    />
                    {errors.free && <small className="text-danger d-block">{errors.free}</small>}
                  </div>
                  <div className="col-md-6 mb-3">
                    
                    </div>
                  <div className="col-md-6 mb-3 d-none">
                    <FormInput
                      label={"Extra Selection Charges"}
                      placeHolder={"Enter Extra Charges"}
                      type={"number"}
                      min="0"
                      step="0.01"
                      value={extraSelectionCharges}
                      onchange={(e) => handleNumericChange(e.target.value, setExtraSelectionCharges)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={canSkip}
                      onToggle={() => setCanSkip(!canSkip)}
                      label={"Can Skip"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={enableQuantity}
                      onToggle={() => setEnableQuantity(!enableQuantity)}
                      label={"Enable Quantity"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-end mb-3">
                    <button
                      type="button"
                      className="btn btn-sm px-3"
                      onClick={() =>
                        setModals((prev) => ({
                          ...prev,
                          showBulkModifierModal: true,
                        }))
                      }
                      style={{
                        backgroundColor: themeColor,
                        border: `1px solid ${themeColor}`,
                        color: "#fff",
                        borderRadius: "8px",
                        fontWeight: 600,
                      }}
                    >
                      <i className="fa fa-list me-2"></i>
                      Select Existing Modifiers
                    </button>
                  </div>
                  <table className="table table-responsive">
                    <thead
                      style={{
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#212529",
                      }}
                    >
                      <tr>
                        <th>Modifier</th>
                        <th>
                          Cost Price ({getCurrencySymbol(compCurrency)})
                        </th>
                        <th>Tax</th>
                        <th>Price ({getCurrencySymbol(compCurrency)})</th>
                        <th>
                          Delivery Price ({getCurrencySymbol(compCurrency)})
                        </th>
                        <th>
                          Take Away Price ({getCurrencySymbol(compCurrency)})
                        </th>
                        <th>Category</th>
                        <th>Stock status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modifiers.map((modifier, index) => {
                        const rowIsExisting = isExistingModifier(modifier);
                        return (
                        <React.Fragment key={index}>
                          <tr
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                          >
                            <td className="align-middle">
                              <div className="d-flex align-items-center">
                                <FormInput
                                  type="text"
                                  value={modifier.menuItem_Name || ""}
                                  placeHolder={
                                    rowIsExisting
                                      ? "Existing modifier"
                                      : "Type new modifier name"
                                  }
                                  readOnly={rowIsExisting}
                                  onchange={(e) =>
                                    handleNewModifierNameChange(
                                      index,
                                      e.target.value
                                    )
                                  }
                                  style={{
                                    cursor: rowIsExisting ? "not-allowed" : "text",
                                    backgroundColor: rowIsExisting
                                      ? (isDarkMode ? "#343a40" : "#F0F0F0")
                                      : (isDarkMode ? "#2d3436" : "#FFFFFF"),
                                  }}
                                />
                                {rowIsExisting && (
                                  <i
                                    className="fa fa-lock text-muted ms-2"
                                    title="Existing modifier — use Select Existing Modifiers to add. Price and details cannot be edited here."
                                    style={{ fontSize: "16px" }}
                                  ></i>
                                )}
                              </div>
                            </td>

                            <td className="align-middle">
                              <FormInput
                                type="number"
                                value={modifier.menuItem_CostPrice ?? ""}
                                placeHolder="0.00"
                                step="any"
                                min="0"
                                readOnly={rowIsExisting}
                                onchange={(e) =>
                                  handleModifierChange(
                                    index,
                                    "menuItem_CostPrice",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            <td className="align-middle">
                              <FormInput
                                type="number"
                                value={modifier.menuItem_TaxPercentage ?? ""}
                                placeHolder="0.00"
                                step="any"
                                min="0"
                                max="100"
                                readOnly={rowIsExisting}
                                onchange={(e) =>
                                  handleModifierChange(
                                    index,
                                    "menuItem_TaxPercentage",
                                    e.target.value
                                  )
                                }
                                title={
                                  modifier.menuItem_TaxPercentage &&
                                  settings.s_VAT &&
                                  modifier.menuItem_TaxPercentage ===
                                    settings.s_VAT.toString()
                                    ? "Default tax rate from settings - you can edit this value"
                                    : "Enter tax percentage"
                                }
                              />
                            </td>

                            <td className="align-middle position-relative">
                              <FormInput
                                type="number"
                                value={modifier.menuItem_SalePrice ?? ""}
                                placeHolder="0.00"
                                step="any"
                                min="0"
                                readOnly={rowIsExisting}
                                onchange={(e) =>
                                  handleModifierChange(
                                    index,
                                    "menuItem_SalePrice",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            <td className="align-middle">
                              <FormInput
                                type="number"
                                value={modifier.menuItem_DeliveryPrice ?? ""}
                                placeHolder="0.00"
                                step="any"
                                min="0"
                                readOnly={rowIsExisting}
                                onchange={(e) =>
                                  handleModifierChange(
                                    index,
                                    "menuItem_DeliveryPrice",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            <td className="align-middle">
                              <FormInput
                                type="number"
                                value={modifier.menuItem_TakeAwayPrice ?? ""}
                                placeHolder="0.00"
                                step="any"
                                min="0"
                                readOnly={rowIsExisting}
                                onchange={(e) =>
                                  handleModifierChange(
                                    index,
                                    "menuItem_TakeAwayPrice",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            <td className="align-middle">
                              <div className="position-relative">
                                <FormInput
                                  label=""
                                  placeHolder="Select Category"
                                  type="text"
                                  value={
                                    modifier.reportCat_Name ||
                                    modifier.rootCat_Name ||
                                    ""
                                  }
                                  onChange={() => {}}
                                  onclick={
                                    rowIsExisting
                                      ? undefined
                                      : () => {
                                          setActiveRow(index);
                                          setModals((prev) => ({
                                            ...prev,
                                            showCategoryModal: true,
                                          }));
                                        }
                                  }
                                  readOnly
                                  icon={rowIsExisting ? "" : "fa fa-caret-down"}
                                  iconOnClick={
                                    rowIsExisting
                                      ? undefined
                                      : () => {
                                          setActiveRow(index);
                                          setModals((prev) => ({
                                            ...prev,
                                            showCategoryModal: true,
                                          }));
                                        }
                                  }
                                  secIcon={
                                    !rowIsExisting &&
                                    (modifier.reportCat_ID || modifier.rootCat_ID)
                                      ? "fa fa-times-circle"
                                      : ""
                                  }
                                  secIconOnClick={
                                    rowIsExisting
                                      ? undefined
                                      : () => {
                                          setModifiers((prevModifiers) =>
                                            prevModifiers.map((mod, i) =>
                                              i === index
                                                ? {
                                                    ...mod,
                                                    reportCat_ID: "",
                                                    reportCat_Name: "",
                                                    rootCat_ID: "",
                                                    rootCat_Name: "",
                                                  }
                                                : mod
                                            )
                                          );
                                        }
                                  }
                                  className="form-control"
                                  name="category"
                                />
                              </div>
                            </td>

                            <td className="align-middle">
                              <div
                                className="d-flex flex-column gap-2 p-2"
                                style={{ minWidth: "120px" }}
                              >
                                <ToggleSwitch
                                  isChecked={!modifier.is_OutOfStock}
                                  onToggle={() =>
                                    handleModifierChange(
                                      index,
                                      "is_OutOfStock",
                                      !modifier.is_OutOfStock
                                    )
                                  }
                                  label="In stock"
                                  disabled={false}
                                  themeColor={themeColor}
                                />
                                <ToggleSwitch
                                  isChecked={
                                    modifier.menuItem_IsDefaultModifier
                                  }
                                  label="Default"
                                  onToggle={() =>
                                    handleModifierChange(
                                      index,
                                      "menuItem_IsDefaultModifier",
                                      !modifier.menuItem_IsDefaultModifier
                                    )
                                  }
                                  themeColor={themeColor}
                                />
                              </div>
                            </td>

                            <td className="align-middle">
                              <div className="d-flex align-items-center m-1">
                                <i
                                  className="fa fa-trash text-danger m-3"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleRemoveModifier(index)}
                                ></i>
                                <i
                                  className="fa fa-bars m-3"
                                  style={{ cursor: "grab" }}
                                ></i>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>

      <CategoryModal
        key={`category-modal-${activeRow}`}
        showParentCategoryModal={modals.showCategoryModal}
        setShowParentCategoryModal={(val) =>
          setModals((prev) => ({
            ...prev,
            showCategoryModal: val,
          }))
        }
        setSelectedParentCategory={(categoryData) => {
          if (activeRow !== null && categoryData) {
            const pickedId = categoryData.rootCat_ID || "";
            const pickedName = categoryData.rootCat_Name || "";

            setModifiers((prevModifiers) =>
              prevModifiers.map((modifier, index) =>
                index === activeRow
                  ? {
                      ...modifier,
                      reportCat_ID: pickedId,
                      reportCat_Name: pickedName || "",
                      rootCat_ID: pickedId,
                      rootCat_Name: pickedName || "",
                    }
                  : modifier
              )
            );
          }
        }}
        onSubmit={(categoryData) => {
          if (activeRow !== null && categoryData) {
            const pickedId = categoryData.rootCat_ID || "";
            const pickedName = categoryData.rootCat_Name || "";

            setModifiers((prevModifiers) =>
              prevModifiers.map((modifier, index) =>
                index === activeRow
                  ? {
                      ...modifier,
                      reportCat_ID: pickedId,
                      reportCat_Name: pickedName || "",
                      rootCat_ID: pickedId,
                      rootCat_Name: pickedName || "",
                    }
                  : modifier
              )
            );
          }
          setModals((prev) => ({ ...prev, showCategoryModal: false }));
        }}
        compID={CompID}
        selectedParentCategory={
          activeRow !== null && modifiers[activeRow]?.reportCat_ID
            ? {
                rootCat_ID: modifiers[activeRow].reportCat_ID,
                rootCat_Name: modifiers[activeRow].reportCat_Name,
              }
            : null
        }
        categories={categories}
        showButton={true}
        isMultiSelect={false}
        modalTitle="Select Category"
        modalSubTitle="Select a category for the modifier"
        fetchCategories={fetchCategories}
      />

      <ModifierSelectionModal
        showModal={modals.showBulkModifierModal}
        setShowModal={(val) =>
          setModals((prev) => ({
            ...prev,
            showBulkModifierModal: val,
          }))
        }
        isMultiSelect={true}
        onMultiSelect={handleMultiModifierSelect}
        showAddNewButton={false}
        excludeModifierIds={addedModifierIds}
        compID={CompID}
        modalTitle="Select Existing Modifiers"
        modalSubTitle="Choose one or more modifiers already in your library"
        confirmButtonLabel="Add to Group"
      />
    </div>
  );
}

export default AddModifierGroup;